import { useEffect, useState } from 'react';
import { searchCitiesGlobal } from './citySearch';

/**
 * Buscador de ciudad de un solo campo: el usuario tipea texto libre y elige una
 * fila del dropdown; de esa fila salen ciudad, país y coordenadas juntos. El
 * país deja de ser un paso previo.
 *
 * `selected` en null es la señal de "todavía no eligió nada válido": cualquier
 * tecleada lo limpia, así que el formulario nunca puede enviarse con un texto
 * escrito a mano que no corresponde a ninguna ciudad real.
 */
export const useCityAutocomplete = ({ initialCity, initialCountry, initialLat, initialLng } = {}) => {
  const [searchText, setSearchText] = useState(initialCity || '');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(
    initialCity ? { city: initialCity, country: initialCountry, lat: initialLat, lng: initialLng } : null
  );

  useEffect(() => {
    if (!searchText) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const timeoutId = setTimeout(() => {
      searchCitiesGlobal(searchText)
        .then((r) => { setResults(r); setIsLoading(false); })
        .catch(() => { setResults([]); setIsLoading(false); });
    }, 280);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const onChangeText = (text) => { setSearchText(text); setSelected(null); };
  const onFocus = () => setIsOpen(true);
  // El delay corto es necesario: si el blur vino de clickear una fila, el
  // onClick tiene que alcanzar a dispararse antes de que la lista se desmonte.
  const onBlur = () => { setTimeout(() => setIsOpen(false), 150); };
  const onSelect = (item) => {
    setSelected({ city: item.label, country: item.country, lat: item.lat, lng: item.lng });
    setSearchText(item.label);
    setIsOpen(false);
  };
  const hydrate = ({ city, country, lat, lng }) => {
    setSearchText(city || '');
    setSelected(city ? { city, country, lat, lng } : null);
  };

  return {
    searchText, results, isLoading, isOpen, setIsOpen,
    onChangeText, onFocus, onBlur, onSelect, hydrate,
    selectedCity: selected?.city ?? null,
    selectedCountry: selected?.country ?? null,
    selectedLat: selected?.lat ?? null,
    selectedLng: selected?.lng ?? null,
  };
};
